import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, BarChart3, Users, DollarSign, Calendar, Shield, FileText, Briefcase } from 'lucide-react';

const REPORT_TYPES = [
  { key: 'participants', label: 'Participants', icon: Users, desc: 'Active participants with funding and status', endpoint: '/participants', fields: ['first_name', 'last_name', 'ndis_number', 'status', 'branch_id'] },
  { key: 'staff', label: 'Staff', icon: Briefcase, desc: 'Staff profiles with compliance status', endpoint: '/staff', fields: ['employee_id', 'compliance_status', 'branch_id'] },
  { key: 'shifts', label: 'Shifts & Roster', icon: Calendar, desc: 'Shift data with assigned staff and times', endpoint: '/roster/shifts', fields: ['date', 'start_time', 'end_time', 'status', 'assigned_staff_id', 'participant_id'] },
  { key: 'timesheets', label: 'Timesheets', icon: FileText, desc: 'Timesheet records with approval status', endpoint: '/timesheets/pending', fields: ['shift_id', 'status', 'hours', 'amount'] },
  { key: 'invoices', label: 'Invoices', icon: DollarSign, desc: 'Invoice records with amounts', endpoint: '/invoices', fields: ['invoice_number', 'participant_id', 'total_amount', 'status'] },
  { key: 'incidents', label: 'Incidents', icon: Shield, desc: 'Incident reports with severity', endpoint: '/incidents', fields: ['incident_no', 'severity', 'status', 'participant_id'] },
  { key: 'candidates', label: 'Recruitment', icon: Users, desc: 'Candidate pipeline data', endpoint: '/candidates', fields: ['first_name', 'last_name', 'stage', 'position_id', 'source'] },
  { key: 'audit', label: 'Audit Logs', icon: FileText, desc: 'System audit trail', endpoint: null, exportOnly: true },
];

function downloadCSV(data, filename) {
  if (!data || data.length === 0) { toast.error('No data to export'); return; }
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row => keys.map(k => {
    const val = row[k];
    if (val === null || val === undefined) return '';
    const str = String(val).replace(/"/g, '""');
    return str.includes(',') || str.includes('\n') ? `"${str}"` : str;
  }).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
  toast.success(`${filename} downloaded (${data.length} rows)`);
}

export default function ReportBuilderPage() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const runReport = async (report) => {
    if (report.exportOnly) {
      // Audit log export
      try {
        const res = await api.get('/security/audit/export?format=JSON&pageSize=5000', { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a'); a.href = url; a.download = 'audit_logs_export.json'; a.click();
        URL.revokeObjectURL(url);
        toast.success('Audit logs exported');
      } catch { toast.error('Export failed'); }
      return;
    }

    setSelectedReport(report);
    setLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: '100' });
      if (dateFrom && report.key === 'shifts') params.set('start', dateFrom);
      if (dateTo && report.key === 'shifts') params.set('end', dateTo);
      if (statusFilter && ['shifts', 'incidents', 'candidates'].includes(report.key)) params.set('status', statusFilter);
      if (statusFilter && report.key === 'candidates') params.set('stage', statusFilter);
      const res = await api.get(`${report.endpoint}?${params}`);
      const items = res.data.items || res.data.data || [];
      setData(items);
    } catch { toast.error('Failed to load report data'); setData([]); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="w-6 h-6" />Reports & Exports</h1>
          <p className="text-muted-foreground mt-1">Generate and export data from any module</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-end">
        <div><Label className="text-xs">From Date</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-40" /></div>
        <div><Label className="text-xs">To Date</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-40" /></div>
        <div><Label className="text-xs">Status Filter</Label><Input value={statusFilter} onChange={e => setStatusFilter(e.target.value)} placeholder="e.g. ACTIVE" className="h-9 w-36" /></div>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {REPORT_TYPES.map(r => {
          const Icon = r.icon;
          return (
            <Card key={r.key} className="cursor-pointer hover:shadow-md transition-shadow hover:border-primary/30" onClick={() => runReport(r)} data-testid={`report-${r.key}`}>
              <CardContent className="py-5 text-center">
                <Icon className="h-8 w-8 mx-auto text-primary mb-2" />
                <p className="font-medium text-sm">{r.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Results */}
      {data !== null && selectedReport && (
        <Card data-testid="report-results">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">{selectedReport.label} Report</CardTitle>
              <CardDescription>{data.length} records</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => downloadCSV(data, `${selectedReport.key}_report_${new Date().toISOString().slice(0, 10)}.csv`)} data-testid="export-report-csv">
              <Download className="h-4 w-4 mr-2" />Export CSV
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-muted-foreground py-4">Loading...</p> : data.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">No data found</p>
            ) : (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background"><tr className="border-b">
                    {Object.keys(data[0]).slice(0, 10).map(k => <th key={k} className="text-left py-2 pr-3 font-medium text-muted-foreground">{k}</th>)}
                  </tr></thead>
                  <tbody>
                    {data.slice(0, 100).map((row, i) => (
                      <tr key={i} className="border-b border-dashed hover:bg-muted/30">
                        {Object.keys(data[0]).slice(0, 10).map(k => (
                          <td key={k} className="py-1.5 pr-3 max-w-[200px] truncate">
                            {typeof row[k] === 'object' ? JSON.stringify(row[k]) : String(row[k] ?? '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.length > 100 && <p className="text-xs text-muted-foreground mt-2 text-center">Showing 100 of {data.length} rows. Export CSV for full data.</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
